/// Synoptic weather — the Day-Skipper-theory material that turns the
/// shipping forecast from a stream of words into a moving weather
/// system you can navigate around. Five domains in one file:
///
///   1. Pressure systems    — highs, lows, ridges, troughs, cols
///   2. Frontal systems     — warm, cold, occluded (with cloud
///                            sequence + wind shift + pressure trend)
///   3. Depression lifecycle — open wave → mature → occluded → dissip.
///   4. Sea / land breezes  — diurnal coastal circulation
///   5. Fog types           — radiation, advection, sea, frontal
///   6. Cloud genera        — the ten Latin-named types every WMO
///                            forecast assumes you know
///
/// Pure data; the SVG diagrams live in apps/web.

// ───────────────────────────────────────────────────────────────────
// Pressure systems
// ───────────────────────────────────────────────────────────────────

export type PressureFeatureKind =
  | "high"
  | "low"
  | "ridge"
  | "trough"
  | "col";

export interface PressureFeature {
  id: PressureFeatureKind;
  /// Display name and the conventional symbol on a synoptic chart
  /// ("H" for High etc.).
  name: string;
  symbol: string;
  /// One-line gist.
  summary: string;
  /// What the isobars look like — how the student spots it on a
  /// chart.
  isobarPattern: string;
  /// Sea-level wind it produces and the rough weather it brings.
  weather: string;
  /// How fast it usually moves over the UK / NW Europe.
  movement: string;
}

export const PRESSURE_FEATURES: readonly PressureFeature[] = Object.freeze([
  {
    id: "high",
    name: "High pressure (anticyclone)",
    symbol: "H",
    summary:
      "An area of relatively high surface pressure — air descending, winds light, conditions stable.",
    isobarPattern:
      "Concentric (often elliptical) isobars around an H centre, widely spaced — light winds at the centre, increasing toward the edges.",
    weather:
      "Light winds + clear skies in summer (warm, hazy, anvil cumulus afternoons). In winter: cold + clear with possible radiation fog overnight.",
    movement:
      "Slow — often weeks of blocking weather over the UK in summer (the 'Azores high').",
  },
  {
    id: "low",
    name: "Low pressure (depression / cyclone)",
    symbol: "L",
    summary:
      "An area of relatively low surface pressure — air rising, fronts attached, winds strong.",
    isobarPattern:
      "Concentric isobars around an L centre, tightly packed — strong winds, especially on the southern side of the low (where the pressure gradient steepens).",
    weather:
      "Strong wind, cloud, rain — usually with a warm-front + cold-front pair attached to the centre. Cold front passage brings a wind shift + clearing showers.",
    movement:
      "Fast — typical Atlantic low crosses the UK in 24–48 hours. Track usually follows the jet stream, west to east.",
  },
  {
    id: "ridge",
    name: "Ridge of high pressure",
    symbol: "—H—",
    summary:
      "An elongated extension of high pressure — settles the weather briefly between successive lows.",
    isobarPattern:
      "Isobars bulge outward AWAY from a high — a U-shape opening toward the H. No closed centre of its own.",
    weather:
      "Brief settled spell — winds lighten, cloud clears, often a sailing window of a few hours between fronts.",
    movement:
      "Travels with the parent high; on a sequence of Atlantic lows, ridges arrive between lows and last ~12 hours.",
  },
  {
    id: "trough",
    name: "Trough of low pressure",
    symbol: "—L—",
    summary:
      "An elongated extension of low pressure — a line of unsettled weather without its own closed centre.",
    isobarPattern:
      "Isobars bulge outward AWAY from a low — a U-shape opening toward the L. Marked on charts as a dashed line through the trough axis.",
    weather:
      "A short, sharp blast — wind freshens, shower line, brief wind shift across the trough line, then it passes.",
    movement:
      "Travels with the parent low at depression speed. A trough trailing a cold front behaves like a secondary cold front.",
  },
  {
    id: "col",
    name: "Col (saddle)",
    summary:
      "The flat area between two highs and two lows — the saddle point on a pressure surface.",
    symbol: "×",
    isobarPattern:
      "Isobars meet in an X-shape with highs on two opposite arms and lows on the other two — straight wide isobars at the centre.",
    weather:
      "Light variable wind, mist or fog likely in autumn / winter (no airflow to clear it). Settled but visibility poor.",
    movement:
      "Drifts with whichever system is moving fastest; usually a transient feature lasting hours, not days.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// Fronts
// ───────────────────────────────────────────────────────────────────

export type FrontKind = "warm" | "cold" | "occluded";

export interface Front {
  id: FrontKind;
  name: string;
  /// Chart symbol — used in the reveal text.
  symbolDescription: string;
  /// What the front actually IS in physical terms.
  description: string;
  /// Cloud sequence the observer sees as the front approaches and
  /// passes, in order.
  cloudSequence: readonly string[];
  /// Wind behaviour at the front passage.
  windShift: string;
  /// Pressure trend before / at / after the front.
  pressure: string;
  /// Precipitation type + duration.
  precipitation: string;
  /// Air-mass change after passage.
  airMassChange: string;
}

export const FRONTS: readonly Front[] = Object.freeze([
  {
    id: "warm",
    name: "Warm front",
    symbolDescription:
      "Solid line with semicircles (●) pointing in the direction of movement (the warm air pushing forward).",
    description:
      "The leading edge of a warm air mass riding up + over the colder air it's replacing. Slope is shallow (1:150), so cloud + precipitation extend hundreds of miles ahead of the surface front.",
    cloudSequence: [
      "Cirrus + cirrostratus (high, thin, halo round the sun) — first warning, 12–24 h before passage.",
      "Altostratus (mid-level, grey) — sun becomes a dim disc through the cloud.",
      "Nimbostratus (low, thick, rain-bearing) — steady rain begins 4–6 h before passage.",
      "After passage: stratus + drizzle + warmer air; rain may ease.",
    ],
    windShift:
      "Backs ahead of the front (e.g. SW to S in the warm sector approach); veers at passage (S to SW).",
    pressure:
      "Falling steadily as the front approaches; steadies in the warm sector behind it.",
    precipitation:
      "Continuous moderate-to-heavy rain extending 200+ nm ahead of the surface front.",
    airMassChange:
      "Cold polar/maritime air → warm tropical/maritime air. Temperature rises 3–8°C; humidity rises; cloud base lowers.",
  },
  {
    id: "cold",
    name: "Cold front",
    symbolDescription:
      "Solid line with triangles (▲) pointing in the direction of movement (the cold air pushing forward).",
    description:
      "The leading edge of a colder air mass undercutting + pushing under the warmer air ahead. Slope is steep (1:50), so weather effects are sharper and shorter than a warm front.",
    cloudSequence: [
      "Warm sector dragging on — broken stratus / cumulus, possibly fair.",
      "Approach: line of cumulus building rapidly into cumulonimbus.",
      "Passage: heavy shower, possibly thunder + squall + hail. Wind veers sharply.",
      "After passage: clearing in 1–2 h, broken cumulus, much cooler air.",
    ],
    windShift:
      "Sharp veer at passage — typically SW → NW (or WSW → N). The most pronounced wind shift in normal weather.",
    pressure:
      "Falling in the warm sector, then a sharp RISE after the cold front clears.",
    precipitation:
      "Short, heavy showers — possibly squally. Duration usually < 1 h.",
    airMassChange:
      "Warm tropical/maritime → colder polar/maritime. Temperature falls 4–8°C; air much drier + clearer behind.",
  },
  {
    id: "occluded",
    name: "Occluded front (occlusion)",
    symbolDescription:
      "Solid line with alternating semicircles AND triangles, both on the same side — the cold front has caught the warm front.",
    description:
      "The cold front (faster) catches the warm front (slower), lifting the warm air off the surface entirely. The surface signature is a single line; aloft, the warm sector air is being squeezed upward.",
    cloudSequence: [
      "Combined warm-front + cold-front cloud signatures — long lead-in (cirrus → nimbostratus) followed by cumulonimbus-style passage.",
      "Often the most persistently miserable feature on the chart — rain ahead + showers behind, no warm-sector break.",
    ],
    windShift:
      "Less pronounced than the cold-front passage — usually backs gently then veers; can be subtle.",
    pressure:
      "Falls to the occlusion, rises afterward but less sharply than a cold front.",
    precipitation:
      "Combined warm + cold front precipitation — long-duration rain followed by showers. Often the wettest feature on the chart.",
    airMassChange:
      "Either warm-type occlusion (air behind is cooler than air ahead) or cold-type (air behind is colder than air ahead) — UK Atlantic occlusions are usually cold-type.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// Depression lifecycle
// ───────────────────────────────────────────────────────────────────

export interface DepressionStage {
  id: "wave" | "open" | "mature" | "occluding" | "occluded" | "dissipating";
  name: string;
  age: string;
  summary: string;
  /// What's happening to the warm + cold fronts at this stage.
  frontStructure: string;
}

export const DEPRESSION_LIFECYCLE: readonly DepressionStage[] = Object.freeze([
  {
    id: "wave",
    name: "Initial wave",
    age: "0–12 h",
    summary:
      "A kink in the polar front (the boundary between tropical and polar air) starts the cyclone. Pressure begins to fall at the wave's crest.",
    frontStructure:
      "Single stationary front with a small wave starting to develop. Warm air noses northwards.",
  },
  {
    id: "open",
    name: "Open wave (young depression)",
    age: "12–24 h",
    summary:
      "Warm front + cold front clearly separated, with a wide warm sector between them. Pressure falls noticeably.",
    frontStructure:
      "Distinct warm front + cold front, with a triangular warm sector between them on the surface chart.",
  },
  {
    id: "mature",
    name: "Mature depression",
    age: "24–48 h",
    summary:
      "Lowest central pressure. Strongest winds. Most clearly-defined frontal system. The cold front is catching the warm front behind.",
    frontStructure:
      "Warm + cold fronts cleanly drawn; warm sector still present but narrowing as the cold front overtakes.",
  },
  {
    id: "occluding",
    name: "Occluding (cold front catching warm)",
    age: "36–60 h",
    summary:
      "Cold front overtakes the warm front near the centre; an occlusion begins forming, working outward from the low's centre.",
    frontStructure:
      "Cold front and warm front merge near the centre; an occluded segment grows from the centre outward.",
  },
  {
    id: "occluded",
    name: "Occluded depression",
    age: "48–72 h",
    summary:
      "Warm sector has been lifted entirely off the surface. The low is at its widest geographic extent but starts to fill.",
    frontStructure:
      "Single occluded front + a triple-point (where warm, cold and occluded fronts meet). Often the warm + cold fronts trail off into the parent flow.",
  },
  {
    id: "dissipating",
    name: "Dissipating",
    age: "72 h +",
    summary:
      "Pressure rises. Wind decreases. The low becomes a slow-moving cold core that drifts away or is absorbed by another system.",
    frontStructure:
      "Fronts fade; the chart often shows just a low centre with no remaining frontal structure.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// Sea + land breezes
// ───────────────────────────────────────────────────────────────────

export interface CoastalBreeze {
  id: "sea-breeze" | "land-breeze";
  name: string;
  time: string;
  driver: string;
  direction: string;
  strength: string;
  /// When it forms reliably in UK / Mediterranean conditions.
  conditions: string;
  /// Operational impact a navigator should plan for.
  navTip: string;
}

export const COASTAL_BREEZES: readonly CoastalBreeze[] = Object.freeze([
  {
    id: "sea-breeze",
    name: "Sea breeze",
    time: "Onset late morning, peak mid-afternoon, dies after sunset.",
    driver:
      "Land heats faster than sea on a sunny day. Air rises over warm land, drawing cooler sea air in to replace it.",
    direction:
      "FROM the sea TOWARD the land — locally perpendicular to the coast at first, then veering with Coriolis as it develops.",
    strength:
      "F2–F4 typically in UK summers; can hit F5 on a strong gradient day on a south- or east-facing coast.",
    conditions:
      "Sunny day, light gradient wind (< F3 from a direction NOT directly opposing the sea breeze direction). Strong gradient wind suppresses it.",
    navTip:
      "Build a sea-breeze plan into a coastal-passage afternoon: free wind from the sea on a windward coast = chance to point higher; from a leeward coast = chance to broad-reach back. Plan to use the sea-breeze front (line of cumulus) as a tactical marker.",
  },
  {
    id: "land-breeze",
    name: "Land breeze",
    time: "Onset 2–3 hours after sunset, peaks before dawn, dies after sunrise.",
    driver:
      "Land cools faster than sea overnight. Air descends over cool land + drains seaward; warm air over the sea rises to replace it.",
    direction:
      "FROM the land TOWARD the sea — usually offshore, normal to the coast.",
    strength:
      "Weaker than the sea breeze — F1–F2 typically. Most pronounced on sheltered, deep-water coasts where the gradient wind is light.",
    conditions:
      "Clear sky overnight (allows land to cool), light gradient wind, deep water close to shore (enhances the differential). Common in Mediterranean summer anchorages.",
    navTip:
      "Plan an early-morning offshore departure to ride the land breeze for the first hour or two — light fair wind taking you clear of the coast before the gradient wind sets in.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// Fog
// ───────────────────────────────────────────────────────────────────

export interface FogType {
  id: "radiation" | "advection" | "sea-fog" | "frontal" | "evaporation";
  name: string;
  summary: string;
  formation: string;
  conditions: string;
  /// Where + when it's most likely.
  whereWhen: string;
  /// How (and when) it normally clears.
  clearance: string;
}

export const FOG_TYPES: readonly FogType[] = Object.freeze([
  {
    id: "radiation",
    name: "Radiation fog",
    summary:
      "Forms over land overnight under clear skies + light wind. Drifts seaward at dawn on coastal estuaries.",
    formation:
      "Ground radiates heat to a clear sky → air immediately above cools below its dew point → moisture condenses near the surface.",
    conditions:
      "Clear sky overnight, light wind (F0–F2), moist air, valley / low ground (cold air pools).",
    whereWhen:
      "Inland and in estuaries; UK autumn + winter. Drifts onto open water at dawn but rarely persists more than a few hundred metres offshore.",
    clearance:
      "Burns off within an hour or two of sunrise as the ground warms; can persist longer in cold weather or under a temperature inversion.",
  },
  {
    id: "advection",
    name: "Advection fog",
    summary:
      "Warm moist air flows over a cold surface and cools below its dew point. The dominant 'pea souper' on UK coasts.",
    formation:
      "Mild + moist tropical-maritime air flowing onto a colder sea surface (or vice versa over cold land). Surface contact cools the air to saturation; fog can persist day or night.",
    conditions:
      "F3–F5 wind (enough to keep the warm air flowing in but not enough to mix it out), sea-surface temperature noticeably colder than air dew point.",
    whereWhen:
      "Western UK + Irish Sea in spring + early summer when sea is still cold and warm SW airstream arrives. Can extend hundreds of nautical miles.",
    clearance:
      "Wind shift to a colder direction (NW airstream) or wind dropping calm; can persist for days while the warm airstream continues.",
  },
  {
    id: "sea-fog",
    name: "Sea fog (advection over sea)",
    summary:
      "Advection fog specifically over the open sea — warm moist air flowing over colder water.",
    formation:
      "Identical mechanism to advection fog, but emphasised because it's the most dangerous form for offshore sailors — visibility down to metres for days at a time, far from any visual reference.",
    conditions:
      "Atlantic / North Sea — warm tropical-maritime air over the cold continental shelf in spring + early summer.",
    whereWhen:
      "Newfoundland Banks (year-round), North Sea + western approaches (March-July), California coast (summer).",
    clearance:
      "Frontal passage bringing a colder air mass, or the warm air losing its moisture as it tracks farther from source.",
  },
  {
    id: "frontal",
    name: "Frontal fog",
    summary:
      "Forms in the rain ahead of a warm front when falling rain saturates the cooler air below the cloud.",
    formation:
      "Heavy rain through the nimbostratus ahead of a warm front falls into cooler air below; the cool air saturates and fog forms in the precipitation layer.",
    conditions:
      "Warm-front approach, steady rain, light wind at the surface.",
    whereWhen:
      "Wherever a warm front is approaching, but most noticeable in valleys + over land where the air is colder than over the sea.",
    clearance:
      "Front passage — warmer air arrives, rain eases, fog lifts as cloud base rises.",
  },
  {
    id: "evaporation",
    name: "Steam fog (evaporation / arctic sea smoke)",
    summary:
      "Cold air flowing over much warmer water — water evaporates, immediately condenses in the cold air, looks like steam rising.",
    formation:
      "Very cold continental air (-10°C or colder) flows over warm water — evaporation from the sea surface saturates the air immediately above, forming wispy fog that rises like steam.",
    conditions:
      "Deep cold polar air, ice-free water nearby, near-calm winds.",
    whereWhen:
      "Arctic + sub-arctic waters; rare in the UK but possible on the Norwegian coast or after extreme cold snaps in the Baltic.",
    clearance:
      "Warmer airmass arriving, or the cold air being mixed out by stronger wind.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// Cloud types (the ten WMO genera)
// ───────────────────────────────────────────────────────────────────

export type CloudLevel = "high" | "middle" | "low" | "vertical";

export interface Cloud {
  id: string;
  name: string;
  abbreviation: string;
  level: CloudLevel;
  /// Typical altitude range over the UK.
  altitude: string;
  appearance: string;
  weatherSignal: string;
}

export const CLOUDS: readonly Cloud[] = Object.freeze([
  {
    id: "cirrus",
    name: "Cirrus",
    abbreviation: "Ci",
    level: "high",
    altitude: "5–13 km",
    appearance:
      "Thin, wispy, white — 'mares' tails' or 'cat's claws'. Made of ice crystals.",
    weatherSignal:
      "Often the first sign of an approaching warm front — 24–36 h ahead of rain. Increase + thickening of Ci = front approaching.",
  },
  {
    id: "cirrostratus",
    name: "Cirrostratus",
    abbreviation: "Cs",
    level: "high",
    altitude: "5–13 km",
    appearance:
      "Thin, milky sheet covering the sky — sun + moon visible through it, often producing a halo.",
    weatherSignal:
      "Warm-front leading edge — rain likely within 12–18 h. Halo round the sun = warm front coming.",
  },
  {
    id: "cirrocumulus",
    name: "Cirrocumulus",
    abbreviation: "Cc",
    level: "high",
    altitude: "5–13 km",
    appearance:
      "High, thin patches with a rippled or 'mackerel sky' pattern of small cells.",
    weatherSignal:
      "'Mackerel skies and mares' tails make tall ships carry low sails' — unsettled weather likely within a day.",
  },
  {
    id: "altostratus",
    name: "Altostratus",
    abbreviation: "As",
    level: "middle",
    altitude: "2–7 km",
    appearance:
      "Grey or blue-grey sheet covering most of the sky. Sun appears as a dim disc behind it.",
    weatherSignal:
      "Mid-stage warm front — rain likely within 6–12 h. Thickening As + lowering = continuous rain imminent.",
  },
  {
    id: "altocumulus",
    name: "Altocumulus",
    abbreviation: "Ac",
    level: "middle",
    altitude: "2–7 km",
    appearance:
      "Mid-level patches of fluffy clumps or rolls — bigger than cirrocumulus, casting shadows on each other.",
    weatherSignal:
      "Often a sign of instability aloft — afternoon thunderstorms possible in summer. 'Castellanus' (turret-shaped Ac) = thunder likely.",
  },
  {
    id: "nimbostratus",
    name: "Nimbostratus",
    abbreviation: "Ns",
    level: "low",
    altitude: "Surface–4 km (deep)",
    appearance:
      "Thick, dark grey, featureless rain-bearing layer. Sky completely covered. Continuous rain or snow falling.",
    weatherSignal:
      "The rain-bearing cloud of a mature warm front or occluded front. Steady moderate-to-heavy rain — wait it out.",
  },
  {
    id: "stratus",
    name: "Stratus",
    abbreviation: "St",
    level: "low",
    altitude: "Surface–2 km",
    appearance:
      "Low, uniform grey layer — like fog that doesn't quite touch the ground. Drizzle or fine rain possible.",
    weatherSignal:
      "Calm, damp, dull weather. Often a sign of stable, warm-moist airmass. Doesn't produce heavy rain but does produce poor visibility.",
  },
  {
    id: "stratocumulus",
    name: "Stratocumulus",
    abbreviation: "Sc",
    level: "low",
    altitude: "Surface–2 km",
    appearance:
      "Low, lumpy grey patches — broken layer with shadows + breaks of blue sky between them.",
    weatherSignal:
      "Common in stable maritime airstreams. Light precipitation possible but usually fair-weather + mostly dry.",
  },
  {
    id: "cumulus",
    name: "Cumulus",
    abbreviation: "Cu",
    level: "low",
    altitude: "Base 0.5–2 km, tops can reach much higher",
    appearance:
      "Fluffy white 'cotton wool' clouds with flat bases and rounded tops. Form in convection over warmed land + water.",
    weatherSignal:
      "Small cumulus = fair-weather. Building tall cumulus = unstable air, showers possible. Growing into towering cumulus = thunderstorm possible.",
  },
  {
    id: "cumulonimbus",
    name: "Cumulonimbus",
    abbreviation: "Cb",
    level: "vertical",
    altitude: "Base 0.5–2 km, tops to 12 km+",
    appearance:
      "Towering convective cloud — flat anvil top spreading out at the tropopause; dark base with rain shaft visible.",
    weatherSignal:
      "Thunderstorm, heavy shower, possible hail or squall. Lightning likely. Reef early when you see Cb building in your direction.",
  },
]);

export const CLOUDS_BY_LEVEL: Record<CloudLevel, readonly Cloud[]> = {
  high: CLOUDS.filter((c) => c.level === "high"),
  middle: CLOUDS.filter((c) => c.level === "middle"),
  low: CLOUDS.filter((c) => c.level === "low"),
  vertical: CLOUDS.filter((c) => c.level === "vertical"),
};
